import React, { useState, useEffect } from "react";
import { Modal, Form, Button, Alert, Row, Col } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { handleUserDetails } from "../../services/users";
import { handleCompanyDetails } from "../../services/companies";
import Loader from "../loader/Loader";
import { Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { MdClose } from "react-icons/md";
import { toast } from "react-toastify";

const validationSchema = Yup.object({
  first_name: Yup.string()
    .required("First name is required")
    .max(255, "First name must be less than 255 characters"),
  last_name: Yup.string()
    .required("Last name is required")
    .max(255, "Last name must be less than 255 characters"),
  email: Yup.string()
    .email("Please provide a valid email address")
    .required("Email is required"),
  law_firm_id: Yup.number()
    .required("Law firm is required"),
  role_id: Yup.number()
    .required("Role is required"),
});

function InviteUserModal({ show, handleClose, onUserInvited }) {
  const [loading, setLoading] = useState(false);
  const [errorResponse, setErrorResponse] = useState([]);
  const [roles, setRoles] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");

  useEffect(() => {
    if (show) {
      fetchData();
    }
  }, [show]);

  const fetchData = async () => {
    try {
      const fetchedRoles = await handleUserDetails.getRoles();
      const fetchedCompanies = await handleCompanyDetails.getCompanies({
        limit: 1000,
      });
      setRoles(fetchedRoles);
      setCompanies(fetchedCompanies.companies_data);
    } catch (err) {
      console.error("Error fetching roles or companies:", err);
    }
  };

  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      law_firm_id: "",
      role_id: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      setErrorResponse([]);
      setSuccessMessage("");
      
      try {
        const response = await handleUserDetails.inviteUser(values);
        
        if (response.status === "Success") {
          toast.success("User invited successfully!");
          setSuccessMessage("User invited successfully!");
          formik.resetForm();
          setTimeout(() => {
            handleClose();
            onUserInvited(); // Refresh the users list
          }, 2000);
        } else {
          toast.error(response.message || "Failed to invite user");
          setErrorResponse([response.message || "Failed to invite user"]);
        }
      } catch (error) {
        console.error("Error inviting user:", error);
        if (error.response && error.response.data && error.response.data.errors) {
          const errors = Object.values(error.response.data.errors).flat();
          setErrorResponse(errors);
        } else {
          setErrorResponse([error.response?.data?.message || "Something went wrong"]);
        }
        toast.error(error.response?.data?.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
  });

  const handleModalClose = () => {
    formik.resetForm();
    setErrorResponse([]);
    setSuccessMessage("");
    handleClose();
  };

  return (
    <>
      <Loader open={loading} />
      <Modal show={show} onHide={handleModalClose} size="lg">
        <div className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h4 className="m-0">Invite User</h4>
            <Button
              variant="link"
              onClick={handleModalClose}
              className="p-0 border-0 bg-transparent"
            >
              <MdClose size={24} />
            </Button>
          </div>

          {errorResponse.length > 0 && (
            <Alert variant="danger">
              {errorResponse.map((error, index) => (
                <div key={index}>{error}</div>
              ))}
            </Alert>
          )}

          {successMessage && (
            <Alert variant="success">
              {successMessage}
            </Alert>
          )}

          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>First Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="first_name"
                    value={formik.values.first_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.first_name && formik.errors.first_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.first_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Last Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="last_name"
                    value={formik.values.last_name}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    isInvalid={formik.touched.last_name && formik.errors.last_name}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.last_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Email *</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                isInvalid={formik.touched.email && formik.errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FormControl fullWidth>
                    <InputLabel>Law Firm *</InputLabel>
                    <Select
                      name="law_firm_id"
                      value={formik.values.law_firm_id}
                      onChange={(e) => formik.setFieldValue("law_firm_id", e.target.value)}
                      label="Law Firm *"
                      error={formik.touched.law_firm_id && Boolean(formik.errors.law_firm_id)}
                    >
                      {companies.map((company) => (
                        <MenuItem key={company.id} value={company.id}>
                          {company.title}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.law_firm_id && formik.errors.law_firm_id && (
                      <div className="text-danger small mt-1">{formik.errors.law_firm_id}</div>
                    )}
                  </FormControl>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <FormControl fullWidth>
                    <InputLabel>Role *</InputLabel>
                    <Select
                      name="role_id"
                      value={formik.values.role_id}
                      onChange={(e) => formik.setFieldValue("role_id", e.target.value)}
                      label="Role *"
                      error={formik.touched.role_id && Boolean(formik.errors.role_id)}
                    >
                      {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>
                          {role.role_name}
                        </MenuItem>
                      ))}
                    </Select>
                    {formik.touched.role_id && formik.errors.role_id && (
                      <div className="text-danger small mt-1">{formik.errors.role_id}</div>
                    )}
                  </FormControl>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex gap-3 justify-content-end mt-4">
              <Button
                variant="secondary"
                onClick={handleModalClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="success"
                disabled={loading}
              >
                {loading ? "Inviting..." : "Invite User"}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>
    </>
  );
}

export default InviteUserModal;
